import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../hooks/useAppState';
import type { GenerationOptions, FileUpload } from '../types';
import { generateFlashcards, isPollinationsConnected, getAvailableModels } from '../services/ai';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { TextArea } from '../components/ui/TextArea';
import { Select } from '../components/ui/Select';
import { Switch } from '../components/ui/Switch';
import { FileUploader } from '../components/FileUploader';
import { FlashcardCard } from '../components/FlashcardCard';
import { PollinationsConnect } from '../components/PollinationsConnect';
import { combineFileContents } from '../services/fileProcessor';
import {
  Wand2,
  Settings,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Loader2,
  Sparkles,
  AlertCircle,
} from 'lucide-react';

export const GeneratePage: React.FC = () => {
  const { addDeck, settings } = useAppState();
  const navigate = useNavigate();

  const [step, setStep] = useState<'upload' | 'options' | 'generating' | 'review'>('upload');
  const [uploadedFiles, setUploadedFiles] = useState<FileUpload[]>([]);
  const [textInput, setTextInput] = useState('');
  const [generatedCards, setGeneratedCards] = useState<any[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(isPollinationsConnected());

  // Generation options
  const [options, setOptions] = useState<GenerationOptions>({
    cardCount: 10,
    difficulty: 'medium',
    includeHints: true,
    includeImages: false,
    language: settings.language || 'en',
    provider: 'pollination',
    model: 'nova-fast',
    deckName: '',
    tags: [],
    outputFormat: 'basic',
  });

  const handleFilesProcessed = (files: FileUpload[]) => {
    setUploadedFiles(files);
    if (files.some((f) => f.processed)) {
      setStep('options');
    }
  };

  const getSourceText = () => {
    const fileText = combineFileContents(uploadedFiles);
    const text = textInput.trim();
    if (fileText && text) return `${fileText}\n\n${text}`;
    return fileText || text;
  };

  const sourceText = getSourceText();

  const handleGenerate = async () => {
    if (!connected) {
      setError('Please connect your Pollinations account first to generate flashcards.');
      return;
    }
    if (!sourceText.trim()) {
      setError('Please provide some text or upload a file first.');
      return;
    }
    if (!options.deckName.trim()) {
      setError('Please provide a deck name.');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setStep('generating');

    try {
      const provider = settings.aiProviders?.[0];
      const cards = await generateFlashcards(sourceText, options, provider);
      setGeneratedCards(cards);
      setStep('review');
    } catch (err) {
      setError((err as Error).message);
      setStep('options');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = () => {
    if (generatedCards.length === 0) return;
    addDeck({
      name: options.deckName,
      description: `Generated from ${uploadedFiles.map((f) => f.name).join(', ') || 'text input'}`,
      cards: generatedCards,
      tags: options.tags,
      language: options.language,
      isFavorite: false,
      color: 'blue',
      source: 'ai',
    });
    navigate('/decks');
  };

  const handleRegenerate = () => {
    setGeneratedCards([]);
    setStep('options');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        {step !== 'upload' && (
          <button
            onClick={() => setStep('upload')}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Generate Flashcards
        </h1>
      </div>

      {/* Connection Status */}
      {!connected && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800 p-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
            <div className="space-y-3">
              <div>
                <p className="font-medium text-yellow-800 dark:text-yellow-200">
                  Pollinations Account Required
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  Connect your Pollinations account to use your Pollen credits for AI-powered flashcard generation.
                </p>
              </div>
              <PollinationsConnect
                onConnectionChange={setConnected}
                showStatus={false}
              />
            </div>
          </div>
        </div>
      )}

      {connected && (
        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 p-3 flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
          <CheckCircle className="w-4 h-4" />
          Connected to Pollinations - using your Pollen credits
        </div>
      )}

      {/* Upload Step */}
      {step === 'upload' && (
        <div className="space-y-8">
          <FileUploader onFilesProcessed={handleFilesProcessed} />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-700" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                or
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Paste Text Content
            </label>
            <TextArea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Paste your study material here. The AI will generate flashcards from this text..."
              rows={6}
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => setStep('options')}
              disabled={!sourceText.trim()}
              rightIcon={<ArrowRight className="w-4 h-4" />}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* Options Step */}
      {step === 'options' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Generation Options
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Deck Name"
                value={options.deckName}
                onChange={(e) => setOptions({ ...options, deckName: e.target.value })}
                placeholder="Enter a name for your new deck"
              />
              <Input
                label="Tags (comma-separated)"
                value={options.tags.join(', ')}
                onChange={(e) =>
                  setOptions({
                    ...options,
                    tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
                  })
                }
                placeholder="e.g. biology, anatomy, chapter-1"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Number of Cards"
                value={String(options.cardCount)}
                onChange={(e) => setOptions({ ...options, cardCount: Number(e.target.value) })}
                options={[
                  { value: '5', label: '5 cards' },
                  { value: '10', label: '10 cards' },
                  { value: '15', label: '15 cards' },
                  { value: '20', label: '20 cards' },
                  { value: '30', label: '30 cards' },
                  { value: '50', label: '50 cards' },
                ]}
              />
              <Select
                label="AI Model"
                value={options.model}
                onChange={(e) => setOptions({ ...options, model: e.target.value })}
                options={getAvailableModels().map((m) => ({ value: m, label: m }))}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Difficulty Level"
                value={options.difficulty}
                onChange={(e) =>
                  setOptions({
                    ...options,
                    difficulty: e.target.value as GenerationOptions['difficulty'],
                  })
                }
                options={[
                  { value: 'easy', label: 'Easy' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'hard', label: 'Hard' },
                  { value: 'mixed', label: 'Mixed' },
                ]}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Output Format"
                value={options.outputFormat}
                onChange={(e) =>
                  setOptions({
                    ...options,
                    outputFormat: e.target.value as GenerationOptions['outputFormat'],
                  })
                }
                options={[
                  { value: 'basic', label: 'Basic Q&A' },
                  { value: 'cloze', label: 'Cloze Deletion' },
                  { value: 'reversible', label: 'Reversible' },
                ]}
              />
              <Select
                label="Language"
                value={options.language}
                onChange={(e) => setOptions({ ...options, language: e.target.value })}
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'es', label: 'Spanish' },
                  { value: 'fr', label: 'French' },
                  { value: 'de', label: 'German' },
                  { value: 'it', label: 'Italian' },
                  { value: 'ja', label: 'Japanese' },
                  { value: 'zh', label: 'Chinese' },
                  { value: 'ko', label: 'Korean' },
                ]}
              />
            </div>

            <div className="flex gap-4">
              <Switch
                checked={options.includeHints}
                onChange={() => setOptions({ ...options, includeHints: !options.includeHints })}
                label="Include hints"
              />
              <Switch
                checked={options.includeImages}
                onChange={() =>
                  setOptions({ ...options, includeImages: !options.includeImages })
                }
                label="Generate images (not supported yet)"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleGenerate}
              loading={isGenerating}
              leftIcon={<Wand2 className="w-5 h-5" />}
            >
              Generate Flashcards
            </Button>
          </div>
        </div>
      )}

      {/* Generating Step */}
      {step === 'generating' && (
        <div className="text-center py-16 space-y-4">
          <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto animate-spin" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Generating flashcards...
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            Our AI is analyzing your content and creating flashcards. This usually takes a few seconds.
          </p>
        </div>
      )}

      {/* Review Step */}
      {step === 'review' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                Generated Flashcards
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                {generatedCards.length} cards generated
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleRegenerate}>
                Regenerate
              </Button>
              <Button onClick={handleSave} leftIcon={<CheckCircle className="w-4 h-4" />}>
                Save Deck
              </Button>
            </div>
          </div>

          {generatedCards.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {generatedCards.map((card) => (
                <FlashcardCard key={card.id} card={card} showActions={false} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No cards were generated. Try again with different content.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GeneratePage;
